// Boss sprites and attack telegraphs, hand-drawn in code like the rest of the city art (no image files).
// Each boss is drawn in its own local frame facing +x and rotated by the container; telegraphs are drawn in world space.
// Everything here is cosmetic: it reads the boss snapshot (attack, wind-up time left, blasts, phase, hp) and the
// boss shot event, and never decides a hit. Transition effects (impacts, swipes, phase change, death) are found by
// comparing a boss's snapshot with the previous one, and live in small capped lists (CLAUDE.md invariant 7).
import { BOSS_FX } from './fx-flags';
import { Container, Graphics, Sprite } from 'pixi.js';
import { shadowTex, facingOf } from './hd-sprites';
import { bossTex, BOSS_DIM, retainBossTexture, releaseBossTexture } from './hd-boss';
import { bossEffects } from './boss-effects';
import { goroWarning, windupOf } from './boss-warning';
import type { BossSnap } from '../../common/src/protocol';
import { BOSSES } from '../../common/src/data';
import { PALETTE, STATE } from './theme';

const OUT = 0x05030a; // the 1 px dark outline every sprite gets
/** drawing scale so each sprite matches its hit radius in bosses.json (the art is drawn at a fixed size) */
const SCALE = { brute: 1.4, mech: 1.4, kaiju: 1 } as const;
const hex = (s: string) => parseInt(s.slice(1), 16);
const DANGER = hex(STATE.danger), WARN = hex(STATE.warning), CYAN = hex(PALETTE.cyan), WHITE = hex(PALETTE.offWhite);
const AMBER = hex(PALETTE.amber), SLATE = hex(PALETTE.slate), INK = hex(PALETTE.midnight), PINK = hex(PALETTE.pink);
/** hit flash tint: off-white with a little danger in it (a tint can only darken, so this reads as a hot blink) */
const HIT_TINT = 0xffb0a8;
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
/** caps on the transition effects, whatever the fight does */
const MAX_WAVES = 16, MAX_BITS = 96, MAX_SCORCH = 10, MAX_SWIPES = 4, MAX_FLASHES = 6, MAX_POPS = 8, MAX_PUFFS = 28;
/** death sequence length (s): flicker and pops, then the collapse */
const DIE = 1.8, LEAVE = 0.6;
/** impact timing (s): the white then yellow flash frames, the shockwave's fast expansion, its linger, scorch life */
const FLASH_W = 0.035, FLASH_Y = 0.075, WAVE_OUT = 0.12, WAVE_LIVE = 0.45, SCORCH_LIFE = 2.5;
/** debris gravity (px/s^2) and the bounce kept on landing */
const GRAV = 420, BOUNCE = 0.35;
/** the largest camera kick a boss strike asks for (the renderer scales it by comfort and distance) */
const KICK = 4;

/** heavy: a thick ring with an inner bright band that expands fast and lingers, instead of a thin fading ring */
type Wave = { x: number; y: number; r: number; t0: number; dur: number; col: number; w: number; heavy?: boolean };
/** debris on the ground plane (x, y) with a height z thrown up at vz; it lands, bounces once and settles */
type Bit = { x: number; y: number; vx: number; vy: number; vz: number; t0: number; life: number; col: number; size: number };
type Scorch = { x: number; y: number; r: number; t0: number; cracks: number[][] };
/** the impact flash: a white frame, then a yellow one */
type Pop = { x: number; y: number; r: number; t0: number };
type Puff = { x: number; y: number; vx: number; vy: number; r: number; t0: number; life: number; col: number };
type Swipe = { x: number; y: number; aa: number; arc: number; reach: number; t0: number };
type Flash = { x: number; y: number; aa: number; len: number; spread: number; t0: number };
interface Entry {
  c: Container; g: Graphics; hp: number; hitT: number;
  /** hit-stop: the boss's own strike freezes its pose for a frame or two from stopT */
  stopT: number; hitBig: boolean; shotUntil: number; lastBoss?: BossSnap; s?: Sprite; sh?: Sprite;
  bodyFx?: Graphics; groundFx?: Graphics;
  /** first seen time left of each ground blast, so the fill shows true progress */
  zStart: Map<string, number>; rageT: number; trailT: number;
  /** set when the boss left the snapshot: death (hp was low) or a timed-out departure */
  gone?: { t0: number; dead: boolean; pops: number };
}
export interface BossFxSink { shake(amp: number, x: number, y: number): void }

/** Review-only warning treatment until the owner approves rollout. */
const REVIEW = BOSS_FX;

export class BossArt {
  /** Opt-in art review; screenshot tooling can compare both treatments in the same scene. */
  reviewWarnings = REVIEW;
  private pool = new Map<number, Entry>();
  private waves: Wave[] = []; private bits: Bit[] = []; private scorch: Scorch[] = []; private swipes: Swipe[] = []; private flashes: Flash[] = [];
  private pops: Pop[] = []; private puffs: Puff[] = [];
  private lowFx = false;
  private comfort = 2;
  /** scorch and cracks lie on the ground, under the people (the other effects draw over them briefly) */
  private floor = new Graphics();
  /** `hd` (2x scene): upright sprites sorted with the people in `chars`, shadows in `under`; without it the 1x top-down art is drawn.
   *  `fx` receives camera shake requests (the renderer limits them by distance and the effects setting). */
  constructor(private layer: Container, private hd?: { chars: Container; under: Container }, private fx?: BossFxSink) {
    if (hd) hd.under.addChild(this.floor); else layer.addChildAt(this.floor, 0);
  }

  /** Match an acknowledged unowned shot to its boss muzzle, not a nearby runner. */
  shot(x: number, y: number, t: number) {
    for (const e of this.pool.values()) {
      const b = e.lastBoss; if (!b || b.w > 0 || b.k === 'kaiju' || e.gone) continue;
      const r = BOSSES.tiers[b.k].radius;
      if (Math.hypot(x - b.x - Math.cos(b.aa) * r, y - b.y - Math.sin(b.aa) * r) < 5) {
        e.shotUntil = t + .12;
        const sg = b.k === 'brute';
        this.push(this.flashes, { x, y, aa: b.aa, len: sg ? 22 : 14, spread: sg ? 0.35 : 0.12, t0: t }, MAX_FLASHES);
        if (sg) {
          this.fx?.shake(2.5, x, y); e.stopT = t;
          this.push(this.pops, { x, y, r: 6, t0: t }, MAX_POPS);
          this.dust(x + Math.cos(b.aa) * 6, y + Math.sin(b.aa) * 6, 3, 8, t, b.aa);
        }
        break;
      }
    }
  }

  draw(bosses: BossSnap[], tel: Graphics, t: number, lowFx = false, comfort = 2) {
    this.lowFx = lowFx;
    this.comfort = lowFx ? 2 : comfort;
    const seen = new Set<number>();
    for (const b of bosses) {
      seen.add(b.id);
      const r = BOSSES.tiers[b.k].radius;
      let e = this.pool.get(b.id);
      if (!e) {
        const c = new Container(), g = new Graphics(); c.addChild(g); this.layer.addChild(c);
        e = { c, g, hp: b.hp, hitT: -1, stopT: -9, hitBig: false, shotUntil: -1, zStart: new Map(), rageT: -9, trailT: 0 }; this.pool.set(b.id, e);
        if (this.hd) {
          const s = new Sprite(); s.anchor.set(0.5, 1); s.scale.set(0.5); const sh = new Sprite(shadowTex(64, 32)); sh.anchor.set(0.5);
          this.hd.chars.addChild(s); this.hd.under.addChild(sh); e.s = s; e.sh = sh;
          e.bodyFx = new Graphics(); e.groundFx = new Graphics();
          this.hd.chars.addChild(e.bodyFx); this.hd.under.addChild(e.groundFx);
        }
      }
      this.transitions(e, b, t, r);
      this.telegraph(b, tel, t, r, e);
      if (b.hp < e.hp && b.w === 0) { e.hitBig = e.hp - b.hp >= b.mhp * 0.02; e.hitT = t; } e.hp = b.hp; // hit flash + stagger
      e.lastBoss = b;
      if (e.bodyFx && e.groundFx) {
        const feet = b.y + BOSS_DIM[b.k].foot;
        e.bodyFx.clear(); e.groundFx.clear(); e.bodyFx.position.set(b.x, feet); e.groundFx.position.set(b.x, feet);
        e.bodyFx.zIndex = feet + .01;
        const effectPose = b.k === 'brute' && e.shotUntil > t ? { ...b, atk: 'shotgun', on: 1 } : b;
        for (const f of bossEffects(effectPose, t, lowFx)) {
          const g = f.layer === 'body' ? e.bodyFx : e.groundFx, color = parseInt(f.color.slice(1), 16);
          if (f.kind === 'dot') g.rect(f.x - f.size / 2, f.y - f.size / 2, f.size, f.size).fill({ color, alpha: f.alpha });
          else if (f.kind === 'glow') { for (let i = 5; i > 0; i--) g.ellipse(f.x, f.y, f.rx * i / 5, f.ry * i / 5).fill({ color, alpha: f.alpha * (1 - i / 6) * .5 }); }
          else if (f.kind === 'ring') g.ellipse(f.x, f.y, f.rx, f.ry).stroke({ color, alpha: f.alpha, width: f.width });
          else { g.moveTo(f.points[0], f.points[1]); for (let i = 2; i < f.points.length; i += 2) g.lineTo(f.points[i], f.points[i + 1]); g.stroke({ color, alpha: f.alpha, width: f.width }); }
        }
        // enrage aura after a phase change: a ground ring that breathes out at the feet
        const rage = t - e.rageT;
        if (rage < 1.6) for (let i = 0; i < 2; i++) { const u = clamp01(rage / 1.6), k = (u * 2 + i * .5) % 1; e.groundFx.ellipse(0, -2, r * (1.2 + k * 1.6), r * (.4 + k * .55)).stroke({ color: DANGER, alpha: (1 - k) * (1 - u) * .8, width: 2 }); }
      }
      e.c.visible = b.w === 0; if (e.s) { e.s.visible = e.sh!.visible = e.c.visible; }
      if (!e.c.visible) continue;
      if (e.s) { this.drawHd(e as Entry & { s: Sprite; sh: Sprite }, b, t, r); continue; }
      const [ox, oy] = this.stagger(e, b, t);
      e.c.x = b.x + ox; e.c.y = b.y + oy; e.c.rotation = b.a; e.c.scale.set(SCALE[b.k]);
      const g = e.g; g.clear();
      const held = t - e.stopT >= 0 && t - e.stopT < 0.05; // hit-stop holds the stride
      if (held) e.c.scale.set(SCALE[b.k] * 1.07);
      const step = Math.sin((held ? e.stopT : t) * (b.k === 'kaiju' ? 3 : 8) + b.id);
      if (b.k === 'brute') brute(g, step, b);
      else if (b.k === 'mech') mech(g, step, b, t);
      else kaiju(g, step, b, t);
      g.tint = this.tint(e, t);
    }
    for (const [id, e] of this.pool) {
      if (seen.has(id)) continue;
      const b = e.lastBoss;
      if (!e.gone && b && b.w === 0) { e.gone = { t0: t, dead: b.hp <= b.mhp * 0.15, pops: 0 }; e.bodyFx?.clear(); e.groundFx?.clear(); }
      if (e.gone && b && this.dying(e as Entry & { lastBoss: BossSnap; gone: NonNullable<Entry['gone']> }, b, t)) continue;
      e.c.destroy({ children: true }); if (e.s) releaseBossTexture(e.s.texture); e.s?.destroy(); e.sh?.destroy(); e.bodyFx?.destroy(); e.groundFx?.destroy(); this.pool.delete(id);
    }
    this.drawFx(tel, t);
  }

  private tint(e: Entry, t: number): number {
    const h = t - e.hitT;
    if (h < 0.05) return HIT_TINT;
    if (h < 0.1 && e.hitBig) return 0xffffff;
    if (h < 0.14 && e.hitBig) return HIT_TINT;
    const rage = t - e.rageT;
    if (rage < 0.6 && Math.sin(rage * 40) > 0) return 0xff8a80; // phase change blink
    return 0xffffff;
  }
  /** knocked back a pixel or two, away from where it is facing (it faces the player shooting it), for a moment */
  private stagger(e: Entry, b: BossSnap, t: number): [number, number] {
    const h = t - e.hitT, len = b.k === 'kaiju' ? 0.1 : 0.14;
    if (h >= len) return [0, 0];
    const amp = (e.hitBig ? 2 : 1) * (1 - h / len) * (b.k === 'kaiju' ? 0.5 : 1);
    return [-Math.cos(b.a) * amp, -Math.sin(b.a) * amp * 0.6];
  }

  /** the HD figure: feet stand `foot` px below the hitbox centre, the view follows the heading, effects pick pre-drawn variants */
  private drawHd(e: Entry & { s: Sprite; sh: Sprite }, b: BossSnap, t: number, r: number) {
    const d = BOSS_DIM[b.k], feet = b.y + d.foot, k = b.k;
    const walk = Math.sin(t * (k === 'kaiju' ? 3 : 8) + b.id);
    const winding = !!b.atk && !b.on, wp = winding ? clamp01(1 - b.at / windupOf(b)) : 0;
    const pose = {
      // wind-ups hold still (no stride) so the pose reads; the brute's raise is the smash wind-up
      stride: winding ? 0 : walk > 0.35 ? 1 : walk < -0.35 ? -1 : 0, raise: b.atk === 'smash' && !b.on,
      fire: e.shotUntil > t,
      glow: Math.round((k === 'brute' ? 0 : b.atk === 'breath' ? (b.on ? 1 : 0.5 + 0.5 * Math.sin(t * 20)) : 0.35 + Math.min(2, b.ph) * .2 + 0.15 * Math.sin(t * (k === 'mech' ? 5 : 2))) * 3),
      breath: k === 'kaiju' && b.atk === 'breath' && b.on ? 2 : 0,
      tail: k === 'kaiju' ? Math.round((Math.sin(t * 1.6) * 6 + (b.atk === 'tail' && !b.on ? -14 : 0)) * 2 / 4) * 4 : 0,
    };
    // hit-stop: for ~2 frames after its own strike lands the pose holds (no texture change) and the body flattens hard
    const stop = t - e.stopT, held = stop >= 0 && stop < 0.05;
    if (!held) {
      const texture = bossTex(k, facingOf(b.a), pose);
      if (texture !== e.s.texture) { retainBossTexture(texture); releaseBossTexture(e.s.texture); e.s.texture = texture; }
    }
    const [ox, oy] = this.stagger(e, b, t);
    // wind-up: a crouch that deepens (squash), with a shiver in the last third; charge leans in; release pops back
    const shiver = winding && wp > 0.66 && !this.lowFx ? (Math.sin(t * 70) > 0 ? 0.5 : -0.5) : 0;
    const squash = held ? 0.07 : stop >= 0 && stop < 0.12 ? -0.03 : winding ? wp * (b.atk === 'smash' ? 0.02 : b.atk === 'stomp' ? 0.06 : 0.04) : (t - e.hitT < 0.08 && e.hitBig ? 0.03 : 0);
    e.s.scale.set(0.5 * (1 + squash), 0.5 * (1 - squash));
    e.s.x = b.x + ox + shiver; e.s.y = feet + oy; e.s.zIndex = feet;
    e.s.alpha = 1;
    e.s.tint = held && stop < 0.02 && !this.lowFx ? HIT_TINT : this.tint(e, t);
    e.sh.x = b.x; e.sh.y = feet - 2; e.sh.width = r * 2.6; e.sh.height = r * 0.9; e.sh.alpha = 1;
  }

  // ------------------------------------------------------------------ transitions (snapshot to snapshot)
  private transitions(e: Entry, b: BossSnap, t: number, r: number) {
    const p = e.lastBoss, fx = !this.lowFx;
    // arrival: a slam ring as it lands
    if (p && p.w > 0 && b.w === 0) { this.impact(b.x, b.y, r * 2.4, t, DANGER, 16); this.wave(b.x, b.y, r * 3, t + 0.05, 0.5, DANGER, 2); e.stopT = t; }
    if (b.w > 0) return;
    // ground blasts that went off since last frame: shockwave, scorch, debris (the server's boom event adds fire and shake)
    const now = new Set(b.z.map(([x, y]) => `${x},${y}`));
    for (const [key] of e.zStart) if (!now.has(key)) {
      const z = p?.z.find(([x, y]) => `${x},${y}` === key); e.zStart.delete(key);
      if (!z || z[3] > 0.35) continue; // dropped for another reason (boss gone, list reset)
      const [x, y, zr] = z;
      this.impact(x, y, zr, t, WARN, b.k === 'kaiju' ? 14 : b.k === 'brute' ? 10 : 7);
      if (b.k !== 'mech') e.stopT = t; // its own fist or foot; mortar shells land far from the body
    }
    for (const [x, y, , zt] of b.z) { const key = `${x},${y}`; if (!e.zStart.has(key)) e.zStart.set(key, Math.max(zt, 0.2)); }
    if (!p) return;
    // phase change: two rings, a blink and an aura for a moment
    if (b.ph > p.ph) { e.rageT = t; this.wave(b.x, b.y, r * 3.2, t, 0.6, DANGER, 3); this.wave(b.x, b.y, r * 2.4, t + 0.15, 0.5, PINK, 2); this.debris(b.x, b.y, 14, r, t); this.fx?.shake(3, b.x, b.y); }
    // tail sweep released: the cone's edge whips across the arc
    if (p.atk === 'tail' && !p.on && b.atk !== 'tail' && p.at < 0.3) {
      const reach = p.rg + r;
      this.push(this.swipes, { x: b.x, y: b.y, aa: p.aa, arc: p.arc, reach, t0: t }, MAX_SWIPES);
      this.fx?.shake(KICK, b.x, b.y); e.stopT = t;
      // a gouge torn along the outer half of the arc, dust and road thrown off the sweep's edge as it passes
      const cracks: number[][] = [];
      for (let i = 0; i < 3; i++) {
        const R = reach * (0.62 + i * 0.13), pts: number[] = [];
        for (let j = 0; j <= 8; j++) { const a = p.aa - p.arc / 2 + p.arc * j / 8; pts.push(b.x + Math.cos(a) * (R + (Math.random() - .5) * 4), b.y + Math.sin(a) * (R + (Math.random() - .5) * 4)); }
        cracks.push(pts);
      }
      this.push(this.scorch, { x: b.x, y: b.y, r: 0, t0: t + 0.13, cracks }, MAX_SCORCH); // shows once the sweep has passed
      const n = fx ? 7 : 3;
      for (let i = 0; i < n; i++) {
        const a = p.aa - p.arc / 2 + p.arc * (i + .5) / n, d = reach * (0.7 + 0.3 * Math.random()), at = t + 0.28 * ((i + .5) / n) / 2.2; // when the sweep's lead passes
        const x = b.x + Math.cos(a) * d, y = b.y + Math.sin(a) * d;
        this.dust(x, y, 1, 10, at, a + Math.PI / 2);
        if (fx) this.debris(x, y, 2, 4, at);
      }
    }
    // charge: dust kicked up along the run, a thump where it stops
    if (b.atk === 'charge' && b.on && fx && t >= e.trailT) {
      e.trailT = t + 0.05;
      const c = Math.cos(b.aa), s = Math.sin(b.aa);
      for (const side of [-1, 1]) this.bit(b.x - c * r - s * side * r * 0.6, b.y - s * r + c * side * r * 0.6, -c * 30 + (Math.random() - .5) * 20, -s * 30 + (Math.random() - .5) * 20, 20 + Math.random() * 30, t, 0.45, SLATE, 3);
    }
    if (p.atk === 'charge' && p.on && !(b.atk === 'charge' && b.on)) { this.impact(b.x + Math.cos(p.aa) * r, b.y + Math.sin(p.aa) * r, r * 1.5, t, WHITE, 9); e.stopT = t; }
    // the breath ignites with a muzzle flash and a kick, rumbles while it pours, and leaves a scorched fan behind
    if (b.atk === 'breath' && b.on && !(p.atk === 'breath' && p.on)) {
      const mx = b.x + Math.cos(b.aa) * r, my = b.y + Math.sin(b.aa) * r;
      this.push(this.pops, { x: mx, y: my, r: r * 0.8, t0: t }, MAX_POPS); this.wave(mx, my, r * 1.4, t, WAVE_LIVE, CYAN, 3, true);
      this.fx?.shake(3, b.x, b.y); e.stopT = t;
    }
    if (b.atk === 'breath' && b.on && Math.floor(t * 4) !== Math.floor((t - 1 / 60) * 4)) {
      this.fx?.shake(2, b.x, b.y);
      // dust rolls off the cone's far edge on every rumble
      const a = b.aa + (Math.random() - .5) * b.arc, d = (b.rg + r) * (0.75 + 0.25 * Math.random());
      this.dust(b.x + Math.cos(a) * d, b.y + Math.sin(a) * d, 1, 9, t, a);
    }
    if (p.atk === 'breath' && p.on && !(b.atk === 'breath' && b.on)) {
      const reach = p.rg + r, cracks: number[][] = [];
      for (let i = 0; i < 5; i++) { const a = p.aa - p.arc / 2 + p.arc * (i + .5) / 5, d0 = r * 1.2, d1 = reach * (0.75 + 0.2 * Math.random()), pts: number[] = []; for (let j = 0; j <= 4; j++) { const d = d0 + (d1 - d0) * j / 4, w = (Math.random() - .5) * 0.08; pts.push(b.x + Math.cos(a + w) * d, b.y + Math.sin(a + w) * d); } cracks.push(pts); }
      this.push(this.scorch, { x: b.x, y: b.y, r: 0, t0: t, cracks }, MAX_SCORCH);
    }
    // stomp wind-up start: the ground shivers once as the foot lifts
    if (b.atk === 'stomp' && p.atk !== 'stomp') this.fx?.shake(1, b.x, b.y);
  }

  /** after the boss left the snapshot. Returns false when the sequence is over and the entry can go. */
  private dying(e: Entry & { lastBoss: BossSnap; gone: NonNullable<Entry['gone']> }, b: BossSnap, t: number): boolean {
    const u = t - e.gone.t0, r = BOSSES.tiers[b.k].radius;
    if (!e.gone.dead) { // timed out: it simply fades away
      if (u > LEAVE) return false;
      if (e.s) { e.s.alpha = 1 - u / LEAVE; e.sh!.alpha = e.s.alpha; } else e.c.alpha = 1 - u / LEAVE;
      return true;
    }
    if (u > DIE) return false;
    // pops: three staggered blasts on the body, then the final one as it collapses
    const pops = [0, 0.3, 0.65, 1.15];
    while (e.gone.pops < pops.length && u >= pops[e.gone.pops]) {
      const i = e.gone.pops++, last = i === pops.length - 1, a = i * 2.4 + b.id;
      const x = last ? b.x : b.x + Math.cos(a) * r * 0.6, y = last ? b.y : b.y + Math.sin(a) * r * 0.5 - r * 0.4;
      if (last) { this.impact(x, y, r * 2.2, t, DANGER, 20); this.wave(x, y, r * 3.5, t + 0.05, 0.7, WARN, 2); continue; }
      this.wave(x, y, r * 1.3, t, 0.35, WARN, 2);
      if (!this.lowFx) this.debris(x, y, 6, r * 0.4, t);
      this.fx?.shake(2, x, y);
    }
    const collapse = clamp01((u - 1.15) / (DIE - 1.15)), flick = u < 1.15 && Math.sin(u * 45) > 0;
    const jx = u < 1.15 && !this.lowFx ? (Math.random() - 0.5) * 2 : 0;
    if (e.s) {
      e.s.visible = e.sh!.visible = true;
      e.s.tint = flick ? HIT_TINT : 0xffffff; e.s.x = b.x + jx;
      e.s.scale.set(0.5 * (1 + collapse * 0.25), 0.5 * (1 - collapse * 0.7)); e.s.alpha = 1 - collapse;
      e.sh!.alpha = 1 - collapse;
    } else {
      e.c.visible = true; e.g.tint = flick ? HIT_TINT : 0xffffff; e.c.x = b.x + jx;
      e.c.scale.set(SCALE[b.k] * (1 - collapse * 0.4)); e.c.alpha = 1 - collapse;
    }
    return true;
  }

  // ------------------------------------------------------------------ bounded effect lists
  private push<T>(list: T[], v: T, max: number) { list.push(v); if (list.length > max) list.shift(); }
  private wave(x: number, y: number, r: number, t0: number, dur: number, col: number, w: number, heavy = false) { this.push(this.waves, { x, y, r, t0, dur, col, w, heavy }, this.lowFx ? MAX_WAVES / 2 : MAX_WAVES); }
  private bit(x: number, y: number, vx: number, vy: number, vz: number, t0: number, life: number, col: number, size: number) { this.push(this.bits, { x, y, vx, vy, vz, t0, life, col, size }, MAX_BITS); }
  /** chunks of road thrown up out of an impact: slate grit with a few hot bits; they fall, bounce once and settle */
  private debris(x: number, y: number, n: number, r: number, t: number) {
    if (this.lowFx) n = Math.ceil(n / 3);
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, sp = 30 + Math.random() * 80, d = Math.random() * r;
      this.bit(x + Math.cos(a) * d, y + Math.sin(a) * d * 0.6, Math.cos(a) * sp, Math.sin(a) * sp * 0.6, 60 + Math.random() * 110, t, 0.55 + Math.random() * 0.3,
        i % 4 === 0 ? AMBER : i % 3 === 0 ? WHITE : SLATE, i % 3 === 0 ? 3 : 2);
    }
  }
  /** dust puffs that billow out and fade (half as many on Low effects); `dir` biases them outward from a strike */
  private dust(x: number, y: number, n: number, r: number, t: number, dir?: number) {
    if (this.lowFx) n = Math.ceil(n / 2);
    for (let i = 0; i < n; i++) {
      const a = dir === undefined ? Math.random() * Math.PI * 2 : dir + (Math.random() - .5) * 1.2, sp = 12 + Math.random() * 22;
      this.push(this.puffs, { x: x + Math.cos(a) * r * 0.5, y: y + Math.sin(a) * r * 0.35, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp * 0.6 - 6, r: r * (0.35 + Math.random() * 0.25), t0: t, life: 0.4 + Math.random() * 0.1, col: i % 3 === 0 ? WHITE : SLATE }, MAX_PUFFS);
    }
  }
  /** One heavy strike landing: a white then yellow flash frame, a thick shockwave with a bright inner band that
   *  expands fast and lingers, a scorch with cracks sized to the blast, chunky debris, dust and a camera kick. */
  private impact(x: number, y: number, R: number, t: number, col: number, chunks: number) {
    if (!this.lowFx) this.push(this.pops, { x, y, r: R * 0.55, t0: t }, MAX_POPS);
    this.wave(x, y, R * 1.1, t, WAVE_LIVE, col, this.lowFx ? 2 : 3, true);
    const cracks: number[][] = [], n = 5 + Math.floor(Math.random() * 3);
    for (let i = 0; i < n; i++) { // jagged lines from the centre out to the blast edge, one fork each
      const a0 = i * Math.PI * 2 / n + (Math.random() - .5) * 0.6, len = R * (0.75 + Math.random() * 0.35), pts: number[] = [x, y];
      let a = a0;
      for (let j = 1; j <= 4; j++) { a += (Math.random() - .5) * 0.5; const d = len * j / 4; pts.push(x + Math.cos(a) * d, y + Math.sin(a) * d * 0.7); }
      cracks.push(pts);
      const fj = 2 + (i % 2), fa = a0 + (i % 2 ? 0.5 : -0.5), fx0 = pts[fj * 2], fy0 = pts[fj * 2 + 1], fl = len * 0.3;
      cracks.push([fx0, fy0, fx0 + Math.cos(fa) * fl, fy0 + Math.sin(fa) * fl * 0.7]);
    }
    this.push(this.scorch, { x, y, r: R * 0.8, t0: t, cracks }, MAX_SCORCH);
    this.debris(x, y, chunks, R * 0.3, t);
    this.dust(x, y, this.lowFx ? 4 : 6, R * 0.6, t);
    this.fx?.shake(KICK, x, y);
  }
  private drawFx(g: Graphics, t: number) {
    const f = this.floor; f.clear();
    for (const s of this.scorch) {
      const u = (t - s.t0) / SCORCH_LIFE; if (u < 0 || u > 1) continue;
      const fade = u < 0.7 ? 1 : 1 - (u - 0.7) / 0.3, hot = clamp01(1 - (t - s.t0) / 0.6);
      if (s.r > 0) f.ellipse(s.x, s.y, s.r, s.r * 0.6).fill({ color: INK, alpha: 0.5 * fade }).ellipse(s.x, s.y, s.r * 0.55, s.r * 0.33).fill({ color: INK, alpha: 0.35 * fade });
      for (const c of s.cracks) {
        f.moveTo(c[0], c[1]); for (let i = 2; i < c.length; i += 2) f.lineTo(c[i], c[i + 1]);
        f.stroke({ color: INK, width: 2, alpha: 0.85 * fade });
        if (hot > 0) { f.moveTo(c[0], c[1]); for (let i = 2; i < c.length; i += 2) f.lineTo(c[i], c[i + 1]); f.stroke({ color: AMBER, width: 1, alpha: 0.9 * hot }); }
      }
    }
    for (const p of this.pops) { // white frame, then yellow, then gone
      const a = t - p.t0; if (a < 0 || a >= FLASH_Y) continue;
      if (a < FLASH_W) g.ellipse(p.x, p.y, p.r, p.r * 0.7).fill({ color: WHITE, alpha: 0.95 });
      else g.ellipse(p.x, p.y, p.r * 1.15, p.r * 0.8).fill({ color: WARN, alpha: 0.7 }).ellipse(p.x, p.y, p.r * 0.5, p.r * 0.35).fill({ color: WHITE, alpha: 0.8 });
    }
    for (const w of this.waves) {
      const u = (t - w.t0) / w.dur; if (u < 0 || u > 1) continue;
      if (w.heavy) {
        // out to full size in WAVE_OUT, then it holds and thins as it fades
        const a = t - w.t0, k = 1 - (1 - Math.min(1, a / WAVE_OUT)) ** 3, R = w.r * (0.25 + 0.75 * k + 0.08 * u), live = 1 - u * u;
        const wid = Math.max(1, w.w * (1 - u * 0.6));
        if (a < 0.2) g.ellipse(w.x, w.y, R, R * 0.7).fill({ color: w.col, alpha: 0.22 * (1 - a / 0.2) });
        g.ellipse(w.x, w.y, R, R * 0.7).stroke({ color: w.col, width: wid, alpha: 0.95 * live });
        g.ellipse(w.x, w.y, Math.max(1, R - wid), Math.max(1, (R - wid) * 0.7)).stroke({ color: WHITE, width: 1, alpha: 0.9 * live * (1 - u) });
        continue;
      }
      const e = 1 - (1 - u) ** 3; // fast out, slow settle
      g.ellipse(w.x, w.y, w.r * e, w.r * e * 0.7).stroke({ color: w.col, width: Math.max(1, w.w * (1 - u)), alpha: 0.9 * (1 - u) });
      if (u < 0.15) g.ellipse(w.x, w.y, w.r * 0.25, w.r * 0.18).fill({ color: WHITE, alpha: 0.6 * (1 - u / 0.15) });
    }
    for (const p of this.puffs) {
      const u = (t - p.t0) / p.life; if (u < 0 || u > 1) continue;
      const dt = t - p.t0, slow = dt * (1 - u * 0.5), r = p.r * (0.6 + u * 0.9);
      g.circle(Math.round(p.x + p.vx * slow), Math.round(p.y + p.vy * slow), r).fill({ color: p.col, alpha: 0.4 * (1 - u) });
    }
    for (const s of this.swipes) {
      const u = (t - s.t0) / 0.32; if (u < 0 || u > 1) continue;
      // a heavy band sweeping from one edge of the cone to the other with two after-images, then fading
      const a0 = s.aa - s.arc / 2, lead = a0 + s.arc * Math.min(1, u * 2.2), fade = u < 0.45 ? 1 : 1 - (u - 0.45) / 0.55;
      for (let k = 2; k >= 0; k--) {
        const ld = Math.max(a0, lead - k * 0.22), tl = Math.max(a0, ld - 0.9), inner = s.reach * (0.3 + k * 0.05);
        if (ld <= tl) continue;
        g.moveTo(s.x + Math.cos(tl) * inner, s.y + Math.sin(tl) * inner)
          .arc(s.x, s.y, s.reach, tl, ld).lineTo(s.x + Math.cos(ld) * inner, s.y + Math.sin(ld) * inner)
          .arc(s.x, s.y, inner, ld, tl, true).fill({ color: k ? WARN : WHITE, alpha: (k ? 0.18 : 0.6) * fade });
      }
      const tail = Math.max(a0, lead - 0.9);
      g.arc(s.x, s.y, s.reach, tail, lead).stroke({ color: DANGER, width: 3, alpha: 0.95 * fade });
      g.arc(s.x, s.y, s.reach - 3, tail, lead).stroke({ color: WHITE, width: 1, alpha: 0.9 * fade });
      // the leading edge: a hard white spoke
      g.moveTo(s.x + Math.cos(lead) * s.reach * 0.3, s.y + Math.sin(lead) * s.reach * 0.3).lineTo(s.x + Math.cos(lead) * (s.reach + 2), s.y + Math.sin(lead) * (s.reach + 2)).stroke({ color: WHITE, width: 2, alpha: fade });
    }
    for (const f of this.flashes) {
      const u = (t - f.t0) / 0.09; if (u < 0 || u > 1) continue;
      const a0 = f.aa - f.spread, a1 = f.aa + f.spread, L = f.len * (0.6 + 0.4 * u);
      g.moveTo(f.x, f.y).lineTo(f.x + Math.cos(a0) * L, f.y + Math.sin(a0) * L).lineTo(f.x + Math.cos(a1) * L, f.y + Math.sin(a1) * L).closePath().fill({ color: AMBER, alpha: 0.7 * (1 - u) });
      g.moveTo(f.x, f.y).lineTo(f.x + Math.cos(f.aa) * L * 0.6, f.y + Math.sin(f.aa) * L * 0.6).stroke({ color: WHITE, width: 2, alpha: 1 - u });
    }
    for (const p of this.bits) {
      const u = (t - p.t0) / p.life; if (u < 0 || u > 1) continue;
      const [gx, gy, z] = bitAt(p, t - p.t0);
      if (z > 1) f.rect(Math.round(gx), Math.round(gy), p.size, 1).fill({ color: INK, alpha: 0.5 * (1 - u) }); // its shadow on the road
      g.rect(Math.round(gx), Math.round(gy - z), p.size, p.size).fill({ color: p.col, alpha: u < 0.7 ? 1 : 1 - (u - 0.7) / 0.3 });
    }
    // drop what has finished
    this.waves = this.waves.filter(w => t - w.t0 < w.dur); this.bits = this.bits.filter(p => t - p.t0 < p.life);
    this.scorch = this.scorch.filter(s => t - s.t0 < SCORCH_LIFE); this.swipes = this.swipes.filter(s => t - s.t0 < 0.32); this.flashes = this.flashes.filter(f => t - f.t0 < 0.09);
    this.pops = this.pops.filter(p => t - p.t0 < FLASH_Y); this.puffs = this.puffs.filter(p => t - p.t0 < p.life);
  }

  /** What's about to hit, all in the danger colour with a shape that tells the timing too (never colour alone):
   *  ground rings with a closing ring and a fill that reach the edge at impact; cones whose wavefront fills outward;
   *  charge lanes with marching chevrons; a blinking laser sight for guns; an arrival beacon with a countdown. */
  private telegraph(b: BossSnap, g: Graphics, t: number, r: number, e: Entry) {
    if (this.reviewWarnings && goroWarning(b, g, this.comfort)) return;
    if (b.w > 0) {
      const p = 0.5 + 0.5 * Math.sin(t * 8);
      g.circle(b.x, b.y, r + 14 + p * 6).stroke({ color: DANGER, width: 2, alpha: 0.5 + p * 0.4 }).circle(b.x, b.y, r).fill({ color: DANGER, alpha: 0.12 });
      // one tick per second left, around the ring
      for (let i = 0; i < Math.min(10, b.w); i++) { const a = -Math.PI / 2 + i * Math.PI / 5, R = r + 10; g.moveTo(b.x + Math.cos(a) * R, b.y + Math.sin(a) * R).lineTo(b.x + Math.cos(a) * (R - 5), b.y + Math.sin(a) * (R - 5)).stroke({ color: WHITE, width: 2, alpha: 0.85 }); }
      return;
    }
    for (const [x, y, zr, zt] of b.z) {
      const start = e.zStart.get(`${x},${y}`) ?? Math.max(zt, 1.2), k = clamp01(1 - zt / start), hot = zt < 0.25;
      g.circle(x, y, zr).fill({ color: DANGER, alpha: 0.08 + 0.06 * k }).stroke({ color: hot && Math.sin(t * 50) > 0 ? WARN : DANGER, width: hot ? 2 : 1.5, alpha: 0.85 });
      g.circle(x, y, zr * k).fill({ color: DANGER, alpha: 0.12 + 0.16 * k });
      // a ring closing in from outside; it meets the edge at impact
      g.circle(x, y, zr * (1 + (1 - k) * 0.6)).stroke({ color: DANGER, width: 1, alpha: 0.25 + 0.5 * k });
      // four ticks turning on the edge
      for (let i = 0; i < 4; i++) { const a = t * 1.5 + i * Math.PI / 2; g.moveTo(x + Math.cos(a) * zr, y + Math.sin(a) * zr).lineTo(x + Math.cos(a) * (zr - 5), y + Math.sin(a) * (zr - 5)).stroke({ color: WHITE, width: 1, alpha: 0.7 }); }
      if (hot) g.moveTo(x - 4, y).lineTo(x + 4, y).moveTo(x, y - 4).lineTo(x, y + 4).stroke({ color: WHITE, width: 1, alpha: 0.9 });
      // mortar shells: a falling streak and a shadow that grows under it in the last half second
      if (b.k === 'mech' && zt < 0.5) {
        const f = zt / 0.5, sy = y - f * 70;
        g.ellipse(x, y, 2 + (1 - f) * 4, 1 + (1 - f) * 2).fill({ color: INK, alpha: 0.6 });
        g.moveTo(x, sy - 8).lineTo(x, sy).stroke({ color: WHITE, width: 2, alpha: 0.9 }).rect(x - 1, sy - 1, 2, 2).fill({ color: AMBER, alpha: 1 });
      }
    }
    if (!b.atk) return;
    const reach = b.rg + r, wp = b.on ? 1 : clamp01(1 - b.at / windupOf(b));
    if (b.arc > 0) { // tail sweep, breath
      const breath = b.atk === 'breath', a0 = b.aa - b.arc / 2, a1 = b.aa + b.arc / 2;
      const fan = (R: number) => g.moveTo(b.x, b.y).lineTo(b.x + Math.cos(a0) * R, b.y + Math.sin(a0) * R).arc(b.x, b.y, R, a0, a1).lineTo(b.x, b.y);
      if (b.on) {
        fan(reach).fill({ color: breath ? CYAN : DANGER, alpha: 0.3 + 0.1 * Math.sin(t * 30) }).stroke({ color: DANGER, width: 1.5, alpha: 0.8 });
        if (breath) for (let i = 0; i < 3; i++) { const R = ((t * 240 + i * reach / 3) % reach); g.arc(b.x, b.y, R, a0 + 0.04, a1 - 0.04).stroke({ color: WHITE, width: 2, alpha: 0.5 * (1 - R / reach) }); }
      } else {
        fan(reach).fill({ color: DANGER, alpha: 0.06 + 0.04 * Math.sin(t * 12) }).stroke({ color: DANGER, width: 1, alpha: 0.6 });
        fan(Math.max(4, reach * wp)).fill({ color: DANGER, alpha: 0.12 + 0.14 * wp });
        g.arc(b.x, b.y, reach * wp, a0, a1).stroke({ color: WHITE, width: 1, alpha: 0.4 + 0.5 * wp });
        if (b.atk === 'tail') { // an arrow along the arc shows which way the sweep goes
          const am = a1 - 0.15, R = reach + 4;
          g.arc(b.x, b.y, R, a0 + 0.1, am).stroke({ color: WHITE, width: 1, alpha: 0.6 })
            .moveTo(b.x + Math.cos(am) * R, b.y + Math.sin(am) * R).lineTo(b.x + Math.cos(am - 0.08) * (R - 4), b.y + Math.sin(am - 0.08) * (R - 4))
            .moveTo(b.x + Math.cos(am) * R, b.y + Math.sin(am) * R).lineTo(b.x + Math.cos(am - 0.08) * (R + 4), b.y + Math.sin(am - 0.08) * (R + 4)).stroke({ color: WHITE, width: 1, alpha: 0.6 });
        }
      }
    } else if (b.atk === 'charge' || b.atk === 'shotgun' || b.atk === 'guns') {
      const c = Math.cos(b.aa), s = Math.sin(b.aa);
      if (b.atk === 'charge') {
        const w = r * 2, nx = -s * w / 2, ny = c * w / 2, ex = b.x + c * reach, ey = b.y + s * reach;
        g.moveTo(b.x + nx, b.y + ny).lineTo(ex + nx, ey + ny).lineTo(ex - nx, ey - ny).lineTo(b.x - nx, b.y - ny).closePath()
          .fill({ color: DANGER, alpha: b.on ? 0.05 : 0.08 + 0.1 * wp });
        if (!b.on) {
          g.moveTo(b.x + nx, b.y + ny).lineTo(ex + nx, ey + ny).moveTo(b.x - nx, b.y - ny).lineTo(ex - nx, ey - ny).stroke({ color: DANGER, width: 1, alpha: 0.6 });
          // chevrons march down the lane, faster as the wind-up ends
          for (let i = 0; i < 5; i++) {
            const d = r + ((i / 5 + t * (0.6 + wp * 1.4)) % 1) * (reach - r), q = w * 0.3, px = b.x + c * d, py = b.y + s * d;
            g.moveTo(px - c * q + nx * 0.6, py - s * q + ny * 0.6).lineTo(px, py).lineTo(px - c * q - nx * 0.6, py - s * q - ny * 0.6).stroke({ color: WHITE, width: 2, alpha: 0.3 + 0.6 * wp });
          }
        }
      } else {
        const mx = b.x + c * r, my = b.y + s * r, ex = b.x + c * reach, ey = b.y + s * reach;
        if (b.atk === 'shotgun' && !b.on) { // the spread the pellets fill
          const sp = 0.35;
          g.moveTo(mx, my).lineTo(b.x + Math.cos(b.aa - sp) * reach * 0.6, b.y + Math.sin(b.aa - sp) * reach * 0.6)
            .lineTo(b.x + Math.cos(b.aa + sp) * reach * 0.6, b.y + Math.sin(b.aa + sp) * reach * 0.6).closePath().fill({ color: DANGER, alpha: 0.06 + 0.1 * wp });
        }
        // laser sight: blinks faster as the shot comes, steady while firing
        if (b.on || Math.sin(t * (12 + wp * 40)) > -0.2) g.moveTo(mx, my).lineTo(ex, ey).stroke({ color: DANGER, width: 1, alpha: b.on ? 0.5 : 0.4 + 0.5 * wp });
        if (!b.on) g.circle(ex, ey, 2 + (1 - wp) * 3).stroke({ color: WHITE, width: 1, alpha: 0.5 + 0.4 * wp });
      }
    }
  }
}

/** a debris chunk `dt` s after it was thrown: ground x, y and height. One bounce keeping BOUNCE of its speed, then it slides to a stop. */
function bitAt(p: Bit, dt: number): [number, number, number] {
  const t1 = 2 * p.vz / GRAV, vz2 = p.vz * BOUNCE, t2 = 2 * vz2 / GRAV;
  let x = p.x, y = p.y, z = 0;
  if (dt < t1) { x += p.vx * dt; y += p.vy * dt; z = p.vz * dt - GRAV * dt * dt / 2; }
  else {
    const d = Math.min(dt - t1, t2);
    x += p.vx * (t1 + d * BOUNCE); y += p.vy * (t1 + d * BOUNCE);
    if (dt - t1 < t2) z = vz2 * d - GRAV * d * d / 2;
  }
  return [x, y, Math.max(0, z)];
}

/** a rect with a 1 px dark outline */
function box(g: Graphics, x: number, y: number, w: number, h: number, col: number) { g.rect(x - 1, y - 1, w + 2, h + 2).fill(OUT); g.rect(x, y, w, h).fill(col); }

/** Goro the Gate: a huge yakuza in a long coat, oni mask, sawn-off shotgun */
function brute(g: Graphics, step: number, b: BossSnap) {
  const swing = b.atk === 'smash' && !b.on ? -3 : 0;
  box(g, -9 + step, -8, 4, 5, 0x15101e); box(g, -9 - step, 3, 4, 5, 0x15101e); // feet
  box(g, -7, -9, 12, 18, 0x2a1f3a); g.rect(-7, -1, 12, 2).fill(0x6a1f3a); // coat, red sash
  box(g, -1 + swing, -12, 9, 3, 0x3a2c50); box(g, -1, 9, 9, 3, 0x3a2c50); // arms
  box(g, 7 + swing, -12, 3, 3, 0xd8a070); box(g, 7, 9, 3, 3, 0xd8a070); // fists
  box(g, 8, 6, 9, 2, 0x8a8a96); // shotgun
  box(g, 0, -4, 7, 8, 0xc8203a); // oni mask
  g.rect(5, -3, 1, 2).fill(0xffe03a).rect(5, 1, 1, 2).fill(0xffe03a); // eyes
  g.rect(1, -6, 2, 2).fill(0xf0f0f0).rect(1, 4, 2, 2).fill(0xf0f0f0); // horns
}

/** Tetsu-Kamen: a four-legged walker with a kabuki face plate and shoulder guns */
function mech(g: Graphics, step: number, b: BossSnap, t: number) {
  for (const [lx, ly, ph] of [[-11, -14, 1], [7, -14, -1], [-11, 10, -1], [7, 10, 1]]) box(g, lx + step * 2 * ph, ly, 5, 4, 0x2c3240); // legs
  box(g, -11, -10, 22, 20, 0x4a5468);
  g.rect(-11, -3, 22, 1).fill(0x2c3240).rect(-4, -10, 1, 20).fill(0x2c3240); // panel lines
  box(g, -3, -16, 15, 4, 0x1e222c); box(g, -3, 12, 15, 4, 0x1e222c); // shoulder guns
  const fire = b.atk === 'guns' && b.on && Math.sin(t * 40) > 0;
  g.rect(12, -15, 2, 2).fill(fire ? 0xffffff : 0xffe03a).rect(12, 13, 2, 2).fill(fire ? 0xffffff : 0xffe03a);
  box(g, 4, -7, 7, 14, 0xf0f0f0); // kabuki face plate
  g.rect(8, -5, 2, 3).fill(0xc8203a).rect(8, 2, 2, 3).fill(0xc8203a).rect(10, -2, 1, 4).fill(0xc8203a);
  const glow = 0.6 + 0.4 * Math.sin(t * 5);
  g.rect(-8, -2, 4, 4).fill({ color: 0x2de2c8, alpha: glow }); // reactor
}

/** NeonG: a kaiju seen from above, glowing dorsal plates, swaying tail */
function kaiju(g: Graphics, step: number, b: BossSnap, t: number) {
  const sway = Math.sin(t * 1.6) * 6 + (b.atk === 'tail' && !b.on ? -14 : 0);
  for (let i = 0; i < 7; i++) { // tail, tapering
    const w = 16 - i * 2, x = -30 - i * 8, y = sway * (i / 6) ** 1.5 - w / 2;
    box(g, x - 8, y, 10, w, i % 2 ? 0x1c3526 : 0x22402e);
  }
  box(g, -12, -27 - step * 2, 10, 8, 0x16281d); box(g, -12, 19 + step * 2, 10, 8, 0x16281d); // hind legs
  g.ellipse(0, 0, 31, 22).fill(OUT); g.ellipse(0, 0, 30, 21).fill(0x22402e); g.ellipse(4, 0, 22, 13).fill(0x2a4d37);
  box(g, 10, -21 + step, 7, 5, 0x16281d); box(g, 10, 16 - step, 7, 5, 0x16281d); // arms
  const hot = b.atk === 'breath' ? (b.on ? 1 : 0.5 + 0.5 * Math.sin(t * 20)) : 0.35 + 0.15 * Math.sin(t * 2);
  for (let i = -3; i <= 3; i++) g.rect(-6 + i * 7, -2 - (i % 2 ? 1 : 0), 4, 4 + (i % 2 ? 2 : 0)).fill({ color: 0x48e8fd, alpha: hot }); // dorsal plates
  g.ellipse(32, 0, 12, 9).fill(OUT); g.ellipse(32, 0, 11, 8).fill(0x22402e); // head
  g.rect(36, -5, 2, 2).fill(0xff3e6c).rect(36, 3, 2, 2).fill(0xff3e6c); // eyes
  if (b.atk === 'breath' && b.on) g.rect(42, -3, 6, 6).fill({ color: 0xe8fbff, alpha: 0.9 });
}
